import API, { graphqlOperation } from '@aws-amplify/api'
import PubSub from '@aws-amplify/pubsub'

import findIndex from 'lodash/findIndex'
import sortBy from 'lodash/sortBy'

import sensorQuery from './sensor-query.graphql'
import vmQuery from './voice-messages.graphql'
import saveMessageMutation from './saveMessage.graphql'
import removeMessageMutation from './removeMessage.graphql'

import Store from '../store'

let deviceSub = null
let refreshInterval = null

const CPL = 4.093333333333334

const state = {
  sensors: [],
  currentDevice: null
}

const getters = {
  getSensorById: (state) => (id) => {
    return state.sensors.find(sensor => sensor.device === id)
  }
}

function cleanValue (value) {
  return value == null || value.length === 0 || isNaN(value) ? '?' : value
}

function handleDeviceEvent (data) {
  const msg = data.value
  if (msg.channel === 'alarm') {
    Store.commit('device/appendAlarm', msg)
  } else if (msg.channel === 'voice-message') {
    Store.commit('device/messageListened', msg)
  } else if (msg.channel === 'sensor') {
    let sensorValue = null

    if (msg.name === 'SHT3x-Temperature') {
      sensorValue = {
        temperature: {val: cleanValue(msg.value), ts: msg.ts}
      }
    } else if (msg.name === 'SCD30-Temperature') {
      sensorValue = {
        temperatureSCD: {val: cleanValue(msg.value), ts: msg.ts}
      }
    } else if (msg.name === 'SHT3x-Humidity') {
      sensorValue = {
        humidity: {val: cleanValue(msg.value), ts: msg.ts}
      }
    } else if (msg.name === 'STM-TCS3472-rgba') {
      let rgba = '?'
      try {
        rgba = msg.value.split(',')
        rgba = rgba.map(parseFloat)
        rgba = Math.round((0.5 * rgba[0] + rgba[1] + rgba[2]) / CPL)
      } catch (e) {
        console.error(e)
      }
      sensorValue = {
        light: {val: rgba, ts: msg.ts}
      }
    } else if (msg.name === 'SCD30-CO2') {
      sensorValue = {
        co2: {val: cleanValue(parseInt(msg.value)), ts: msg.ts}
      }
    } else if (msg.name === 'SGP30-TVOC') {
      sensorValue = {
        voc: {val: cleanValue(parseInt(msg.value)), ts: msg.ts}
      }
    }

    if (msg.value == null || msg.value.length === 0) {
      msg.value = '?'
    }
    Store.commit('device/sensorValue', sensorValue)
  }
  Store.commit('device/ping')
}

function deviceConnectionCheck () {
  // Check if the device sent data within the last 3min
  const now = Date.now() - 1000 * 60 * 3 // 3min
  Store.commit('device/deviceConnection', state.currentDevice.lastSeen >= now)
}

const mutations = {
  __reset (state) {
    state.sensors = []
  },
  appendAlarm (state, alarm) {
    state.currentDevice.alarms12h.unshift(alarm)
  },
  ping (state) {
    state.currentDevice.lastSeen = Date.now()
  },
  deviceConnection (state, hasConnection) {
    state.currentDevice.hasConnection = hasConnection
  },
  sensorValue (state, sv) {
    Object.assign(state.currentDevice.values, sv)
  },
  setSensors (state, sensors) {
    state.sensors = sensors
  },
  setVoiceMessages (state, messages) {
    state.currentDevice.voiceMessages = messages
  },
  addNewVoiceMessage (state, message) {
    message.state = 'new'
    state.currentDevice.voiceMessages.new.unshift(message)
  },
  removeVoiceMessage (state, messageId) {
    let i = findIndex(state.currentDevice.voiceMessages.played, {messageId})
    if (i < 0) {
      i = findIndex(state.currentDevice.voiceMessages.new, {messageId})
      if (i >= 0) {
        state.currentDevice.voiceMessages.new.splice(i, 1)
      }
    } else {
      state.currentDevice.voiceMessages.played.splice(i, 1)
    }
  },
  messageListened (state, message) {
    const i = findIndex(state.currentDevice.voiceMessages.new, {messageId: message.messageId})
    if (i >= 0) {
      const msg = state.currentDevice.voiceMessages.new.splice(i, 1)[0]
      msg.firstEvent = {created: message.created}
      state.currentDevice.voiceMessages.played.unshift(msg)
    }
  },
  setCurrentDevice (state, deviceId) {
    if (deviceSub) {
      deviceSub.unsubscribe()
    }

    if (refreshInterval) {
      clearInterval(refreshInterval)
    }

    refreshInterval = setInterval(deviceConnectionCheck, 5000)

    deviceSub = PubSub.subscribe(`live/${deviceId}`).subscribe({
      next: handleDeviceEvent,
      error: error => console.error(error),
      close: () => console.log('Done'),
    })

    state.currentDevice = state.sensors.find(sensor => sensor.device === deviceId)

  },
  updateSensorValues (state, sensors) {
    sensors.forEach(function (sensor) {
      const i = findIndex(state.sensors, {device: sensor.device})
      Object.assign(state.sensors[i], sensor)
    })
  }
}

function prepareValues (sensors) {
  // Sanitize null sensor values
  return sensors.map(s => {
    s.voiceMessages = {'played': [], 'new': []}
    if (s.values) {
      Object.keys(s.values).forEach(key => {
        let v = s.values[key]
        if (v == null) {
          s.values[key] = {
            'ts': 0,
            'val': '?'
          }
        }
      })
    }
    return s
  })
}

function setLatestTimestamp (sensors) {
  return sensors.map(s => {
    s.ts = 0
    s.hasConnection = false
    if (s.values) {
      s.ts = Object.values(s.values).map(v => v.ts).sort().reverse()[0]
      s.hasConnection = s.ts >= Date.now() - 1000 * 60 * 5
    }
    return s
  })
}

function prepareAlarms (sensors) {
  sensors = sensors.map(s => {
    s.alarms12h = []
    if (s.alarms) {
      s.alarms12h = s.alarms.filter(a => a.ts > new Date().getTime() - 12 * 60 * 60 * 1000).reverse()
    }
    return s
  })
  return sensors
}

const actions = {
  async getSensors ({commit}) {
    const sv = await API.graphql(graphqlOperation(sensorQuery))
    let sensors = prepareValues(sv.data.readSensors)
    sensors = setLatestTimestamp(sensors)
    sensors = prepareAlarms(sensors)
    commit('setSensors', sensors)
  },
  async getVoiceMessages ({commit}) {
    const sv = await API.graphql(graphqlOperation(vmQuery, {deviceId: state.currentDevice.device}))
    const messages = sv.data.listVoiceMessages
    commit('setVoiceMessages', {
      'new': sortBy(messages.filter(m => m.state === 'new'), ['startDate']).reverse(),
      'played': sortBy(messages.filter(m => m.state === 'played'), ['startDate']).reverse()
    })
  },
  async removeVoiceMessages ({commit}, messages) {
    for (let messageId of messages) {
      await API.graphql(graphqlOperation(removeMessageMutation, {messageId}))
      commit('removeVoiceMessage', messageId)
    }
  },
  async saveVoiceMessage ({commit}, message) {
    const result = await API.graphql(graphqlOperation(saveMessageMutation, message))
    commit('addNewVoiceMessage', result.data.createVoiceMessage)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
